import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { fetchAllPages } from "./fetchAllPages";
import type { SingleResponse } from "./types";

// ApiCredential is the secret-free shape returned by list/get. The
// clientSecret is NEVER included here — it is shown exactly once, on
// create/rotate, as ApiCredentialSecret below.
export interface ApiCredential {
  id: string;
  name: string;
  clientId: string;
  groupId: string;
  scopes: string[];
  status: string;
  // NOTE: the backend does not yet return lastUsedAt (no writer) or createdBy;
  // they're omitted here so the UI can't show a misleading value. Re-add with
  // the column once the backend populates them (go-live #5).
  createdBy?: string;
  createdAt: string;
}

// ApiCredentialSecret is returned ONCE on create/rotate — the plaintext
// clientSecret and the token endpoint the caller exchanges it at.
export interface ApiCredentialSecret extends ApiCredential {
  clientSecret: string;
  tokenEndpoint?: string;
}

// The scope catalogue a tenant admin can grant (contract §2). Kept here so the
// create form and any validation share one source of truth. `kind` drives the
// create form's one-click presets (Read-only vs Full access) without hard-coding
// scope names in the UI.
export const CREDENTIAL_SCOPES: { value: string; label: string; kind: "read" | "write" }[] = [
  { value: "riot-fota/devices:read", label: "Read devices", kind: "read" },
  { value: "riot-fota/config:read", label: "Read configuration", kind: "read" },
  { value: "riot-fota/config:write", label: "Write / deploy configuration", kind: "write" },
];

// Preset scope selections for the create form. Read-only = every read scope;
// Full access = the whole catalogue. Derived from CREDENTIAL_SCOPES so new
// scopes flow into the right preset automatically.
export const READ_ONLY_SCOPES = CREDENTIAL_SCOPES.filter((s) => s.kind === "read").map((s) => s.value);
export const ALL_SCOPES = CREDENTIAL_SCOPES.map((s) => s.value);

export function useApiCredentials() {
  return useQuery({
    queryKey: ["api-credentials"],
    // Every credential: the page lists and searches them in the browser.
    queryFn: () => fetchAllPages<ApiCredential>("/api/v1/companies/me/api-credentials"),
    placeholderData: keepPreviousData,
  });
}

export function useCreateApiCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; groupId: string; scopes: string[] }) =>
      apiFetch<SingleResponse<ApiCredentialSecret>>("/api/v1/companies/me/api-credentials", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-credentials"] }),
  });
}

export function useRotateApiCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<SingleResponse<ApiCredentialSecret>>(
        `/api/v1/companies/me/api-credentials/${id}/rotate-secret`,
        { method: "POST" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-credentials"] }),
  });
}

export function useRevokeApiCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/companies/me/api-credentials/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-credentials"] }),
  });
}

// Permanent delete — removes the credential row entirely (and its Cognito
// client). `?permanent=true` opts into hard delete; the plain DELETE above is a
// soft revoke that keeps the row for history.
export function useDeleteApiCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/companies/me/api-credentials/${id}?permanent=true`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-credentials"] }),
  });
}
